import React, { useContext, useEffect, useState } from 'react'

import { Form, Popconfirm, Button, Modal, InputNumber, TimePicker, Divider, Tooltip } from 'antd'
import moment from 'moment'
import EventCodeSelect from './Components/EventCodeSelect'
import ActsButton from './Components/Acts/ActsButton'
import { FULL_DATE_FORMAT, HOURS_MINUTES_FORMAT } from '../../../utils/momentjs'
import { createShiftAct, getShiftActs } from '../../../utils/api/shift'
import ActsTable from './ActsTable'
import { onError, onSuccess, resolvePromises } from '../../../utils/apiHelper'
import { deleteAct } from '../../../utils/api/act'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { getStudentSchoolOptions } from '../../../utils/api/student'
import { GlobalContext } from '../../../Providers/GlobalProvider'
import { SchoolOptions } from '../../../utils/constants'
import { faCheck, faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons'
import { connect } from 'react-redux'
import { getUser } from '../../../reducers/UserReducer'
import { getTranslate } from 'react-localize-redux'
import { isObject } from '../../../utils'

const { ACT_HOURS_VERIFICATION } = SchoolOptions

const INITIAL_VALIDATE_BUTTON_TEXT = 'Submit'
const HOVERING_VALIDATE_BUTTON_TEXT = 'Warning'
const HOVERING_VALIDATE_BUTTON_STYLE = {
  backgroundColor: '#FF7F7F',
  fontWeight: 'bold',
  color: 'white'
}
const INITIAL_VALIDATE_BUTTON_STYLE = {
  backgroundColor: '#3c8dbc',
  fontWeight: 'bold',
  color: 'white'
}

const renderButtonProps = (text, style, icon) => {
  return {
    text,
    icon,
    style: {
      ...style,
      justifyContent: 'center',
      width: '150px'
    }
  }
}

const mapStateToProps = state => ({ user: getUser(state.getUser), t: getTranslate(state.locale) })

const DesiderataUpdateModal = ({ actTypes, date, user, internship, internshipState, isUpdatable, shift, visible, onCancel, onSubmit, t }) => {
  const [values, setValues] = useState({})
  const [acts, setActs] = useState([])
  const [actsByCategory, setActsByCategory] = useState([])
  const [invalidTotalCategories, setInvalidTotalCategories] = useState([])
  const [loading, setLoading] = useState(true)
  const [validateButton, setValidateButton] = useState(renderButtonProps(
    INITIAL_VALIDATE_BUTTON_TEXT,
    INITIAL_VALIDATE_BUTTON_STYLE,
    faCheck
  ))
  const [actHoursVerificationEnabled, setActHoursVerificationEnabled] = useState(true)

  const { eventCodes } = useContext(GlobalContext)

  useEffect(() => {
    if (shift) {
      setValues({
        id: shift.id,
        startTime: moment(shift.start, FULL_DATE_FORMAT),
        endTime: moment(shift.end, FULL_DATE_FORMAT),
        pause: shift.pause,
        eventCodeType: shift.eventCodeType,
        eventCode: shift.eventCode,
        deleted: shift.deleted
      })
      setLoading(true)

      getShiftActs(shift, user).then(json => {
        if (json && json.data) {
          setActs(json.data)
        }

        setLoading(false)
      })
    }
  }, [shift, user])

  useEffect(() => {
    if (acts && acts !== null && acts.length > 0) {
      const actsByCategory = {}

      for (const act of acts) {
        const categoryName = act.actType.actTypeCategory?.name ?? 'Without category'
        const name = act.actType?.name ?? 'Unknown'

        if (!(categoryName in actsByCategory)) {
          actsByCategory[categoryName] = {}
          actsByCategory[categoryName].totalHours = 0
        }

        const actInfo = {
          id: act.id,
          name: name,
          description: act.description
        }

        actsByCategory[categoryName][act.id] = actInfo

        if (act.actType.hoursFormat) {
          const fullHours = act.description.split('h')
          const hours = parseInt(fullHours[0], 10)
          const minutes = parseInt(fullHours[1], 10)
          const addTime = (hours * 3600) + (minutes * 60)

          actsByCategory[categoryName].totalHours += addTime
        }
      }

      setActsByCategory(actsByCategory)
    }
  }, [acts])

  useEffect(() => {
    if (
      actsByCategory &&
      actsByCategory !== null &&
      Object.keys(acts).length > 0 &&
      values.startTime &&
      values.endTime &&
      values.pause
    ) {
      const invalidTotalCategories = []

      const shiftDuration = (values.endTime.diff(values.startTime) / 1000) - (values.pause * 60)

      for (const key in actsByCategory) {
        if (actsByCategory[key].totalHours !== shiftDuration) {
          invalidTotalCategories.push(key)
        }
      }

      setInvalidTotalCategories(invalidTotalCategories)
    }
  }, [actsByCategory, values.startTime, values.endTime, values.pause])

  useEffect(() => {
    if (user !== undefined && internship.school !== undefined) {
      getStudentSchoolOptions(user, user.student).then(({ data }) => {
        const schoolOptions = data[internship.school?.id ?? -1] ?? {}

        setActHoursVerificationEnabled(isObject(schoolOptions) && !!schoolOptions[ACT_HOURS_VERIFICATION])
      })
    }
  }, [user, internship])

  const addActs = (createdActs) => {
    setLoading(true)
    const promises = []

    createdActs.forEach(a => {
      const value = a.hoursFormat ? a.values.hours + 'h' + a.values.minutes : a.values.unit
      const body = {
        shift: shift.id,
        actType: a.actType,
        description: value,
        startDate: shift.start,
        endDate: shift.end
      }

      promises.push(createShiftAct(shift, body, user))
    })

    resolvePromises(promises).then(rawActs => {
      const newActs = [...acts]
      let count = 0

      rawActs.forEach(a => {
        if (a && a.data) {
          newActs.push(a.data)
          count++
        }
      })

      if (count === createdActs.length) {
        onSuccess(count === 1
          ? t('The act has been successfully created')
          : t('The acts have been successfully created')
        )
      } else {
        onError('One or more acts could not be created')
      }

      setActs(newActs)
      setLoading(false)
    })
  }

  const handleDeleteAct = act => {
    deleteAct(act, user).then(json => {
      if (json && json.data) {
        setActs(acts.filter(a => a.id !== act.id))
        onSuccess(t('The act has been successfully deleted'))
      } else {
        onError(t('The act could not be deleted'))
      }
    })
  }

  const handleDelete = () => {
    setValues({ ...values, deleted: true })
    handleClose(true, { ...values, deleted: true }, true)
  }

  const handleClose = (isSubmit, newValues = null, isDelete = false) => {
    if (isSubmit) {
      newValues ? onSubmit(newValues, isDelete) : onSubmit(values, isDelete)
    } else {
      onCancel()
    }
  }

  const title = isUpdatable
    ? t('Modify the schedule for ') + date
    : t('Schedule for ') + date
  const okButtonProps = isUpdatable ? { htmlType: 'submit', form: 'shiftUpdateForm' } : null
  // TODO Put the footer in a separate component
  const footer = isUpdatable
    ? (
      <div>
        <Button key='back' onClick={() => handleClose(false)}>
          {t('Cancel')}
        </Button>
        <Popconfirm
          title={t('Are you sure you want to delete this shift?')}
          onConfirm={handleDelete}
          onCancel={() => handleClose(false)}
        >
          <Button type='danger'>
            {t('Delete shift')}
          </Button>
        </Popconfirm>
        {invalidTotalCategories.length > 0 && actHoursVerificationEnabled && (
          <Popconfirm
            cancelText={t('Cancel')}
            okText={t('Yes')}
            onConfirm={() => handleClose(true)}
            placement='top'
            title={t('student_internship_shift.invalid_act_hours.confirmation')}
          >
            <Tooltip placement='bottom' title={t('student_acts.invalid_hours_total')}>
              <Button
                style={validateButton.style}
                disabled={false}
                loading={loading}
                onMouseEnter={() => setValidateButton(renderButtonProps(
                  HOVERING_VALIDATE_BUTTON_TEXT,
                  HOVERING_VALIDATE_BUTTON_STYLE,
                  faExclamationTriangle
                ))}
                onMouseLeave={() => setValidateButton(renderButtonProps(
                  INITIAL_VALIDATE_BUTTON_TEXT,
                  INITIAL_VALIDATE_BUTTON_STYLE,
                  faCheck
                ))}
              >
                <FontAwesomeIcon icon={validateButton.icon} />&nbsp;
                {t(validateButton.text)}
                {loading && <div className='loading-icon black' />}
              </Button>
            </Tooltip>
          </Popconfirm>
        )}
        {(!invalidTotalCategories.length > 0 || !actHoursVerificationEnabled) && (
          <Button
            style={validateButton.style}
            key='submit'
            type='primary'
            onClick={() => handleClose(true)}
          >
            <FontAwesomeIcon icon={faCheck} />&nbsp;
            {t('Submit')}
          </Button>
        )}
      </div>
    )
    : null

  const renderUpdateContent = () => {
    return (
      <Form
        id='shiftUpdateForm'
        layout='vertical'
        initialvalues={values}
      >
        <Divider orientation='left'> {t('Schedule')} </Divider>
        <Form.Item name='startTime' label={t('Start time')}>
          <TimePicker
            size='large'
            minuteStep={5}
            defaultOpenValue={values.startTime}
            placeholder={moment(values.startTime).format(HOURS_MINUTES_FORMAT)}
            format='HH:mm'
            onChange={(value) => {
              const startDate = values.startTime.clone()
              startDate.hours(value.format('HH'))
              startDate.minutes(value.format('mm'))
              startDate.seconds(0)

              setValues({ ...values, startTime: moment(startDate.format(FULL_DATE_FORMAT)) })
            }}
          />
        </Form.Item>
        <Form.Item name='endTime' label={t('End time')}>
          <TimePicker
            size='large'
            minuteStep={5}
            defaultOpenValue={values.endTime}
            placeholder={moment(values.endTime).format(HOURS_MINUTES_FORMAT)}
            format='HH:mm'
            onChange={(value) => {
              const endDate = values.endTime.clone()
              endDate.hours(value.format('HH'))
              endDate.minutes(value.format('mm'))
              endDate.seconds(0)
              if (endDate < values.startTime) {
                endDate.add(1, 'day')
              }

              setValues({ ...values, endTime: moment(endDate.format(FULL_DATE_FORMAT)) })
            }}
          />
        </Form.Item>
        <Form.Item name='pause' label={t('Pause')} style={{ marginBottom: 0 }}>
          <InputNumber
            size='large'
            min={0}
            step={5}
            defaultValue={values.pause}
            placeholder={values.pause}
            onChange={(value) => {
              setValues({ ...values, pause: value })
            }}
          />
        </Form.Item>
        <Form.Item name='eventCode' style={{ marginBottom: 0 }}>
          <EventCodeSelect
            update
            value={values.eventCodeType ? values.eventCodeType.type : null}
            handleEventCodeSelection={(value, option) => {
              setValues({ ...values, eventCodeType: eventCodes.find(e => e.type === option.props.data.type ?? value), eventCode: { code: t('event.code.' + option.props.data.type ?? value) } })
            }}
          />
        </Form.Item>
      </Form>
    )
  }

  const renderShowContent = () => {
    return (
      <div>
        <h3>{t('Start time')}</h3>
        <p>{moment(values.startTime).format(HOURS_MINUTES_FORMAT)}</p>
        <h3>{t('End time')}</h3>
        <p>{moment(values.endTime).format(HOURS_MINUTES_FORMAT)}</p>
        <h3>{t('Pause')}</h3>
        <p>{values.pause} min</p>
        <h3>{t('Event code')}</h3>
        <p>{values.eventCode ? values.eventCode.code : '/'}</p>
      </div>
    )
  }

  return (
    <Modal
      title={title}
      visible={visible}
      onCancel={() => handleClose(false)}
      okButtonProps={okButtonProps}
      footer={footer}
      afterClose={() => {
        setValues({})
        setActs([])
      }}
    >
      {shift && isUpdatable ? renderUpdateContent() : renderShowContent()}
      {internship.school && internship.school.premium && (
        <>
          <Divider orientation='left'> {t('Acts')} </Divider>
          <ActsButton shift={shift} actTypes={actTypes} acts={acts} onSubmit={addActs} internshipState={internshipState} />
          <br />
          <ActsTable
            actHoursVerificationEnabled={actHoursVerificationEnabled}
            acts={acts}
            invalidTotalCategories={invalidTotalCategories}
            loading={loading}
            onDelete={handleDeleteAct}
          />
        </>
      )}
    </Modal>
  )
}

export default connect(mapStateToProps)(DesiderataUpdateModal)
